import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import {
  buildComplianceAnswerFormKey,
  buildLegacyComplianceAnswerFormKey,
  COMPLIANCE_ANSWER_OPTION_PREFIX,
} from 'shared/domain/protocolAnswer/formKeys';
import {
  isComplianceProtocolAnswer,
  ProtocolAnswer,
} from 'shared/domain/protocolAnswer';
import { HashMap } from 'shared/types/commonView';

export type InspectionScoreProtocolItem = {
  templateChecklistItem?: string | { _id?: string };
  answers?: ProtocolAnswer[];
  complianceCheck?: { result?: ComplianceAnswerOption };
};

export function formatInspectionScorePercent(
  score: number | null
): string | null {
  if (score === null) {
    return null;
  }
  return `${score.toFixed(1)}%`;
}

export function roundInspectionScorePercent(score: number): number {
  return Math.round(score * 10) / 10;
}

function isScoredComplianceResult(
  value: unknown
): value is ComplianceAnswerOption.passed | ComplianceAnswerOption.notPassed {
  return (
    value === ComplianceAnswerOption.passed ||
    value === ComplianceAnswerOption.notPassed
  );
}

function checklistItemIdFromProtocolItem(
  protocolItem: InspectionScoreProtocolItem
): string | undefined {
  const templateChecklistItem = protocolItem.templateChecklistItem;
  if (typeof templateChecklistItem === 'string') {
    return templateChecklistItem;
  }
  return templateChecklistItem?._id;
}

function resolveFormComplianceResult(
  formValues: HashMap<unknown> | undefined,
  checklistItemId: string | undefined,
  answerId: string
): unknown {
  if (!formValues || !checklistItemId) {
    return undefined;
  }

  const formKey = buildComplianceAnswerFormKey(checklistItemId, answerId);
  if (formValues[formKey] !== undefined) {
    return formValues[formKey];
  }

  return formValues[buildLegacyComplianceAnswerFormKey(checklistItemId)];
}

/**
 * Calculate inspection score from an array of compliance check results
 * @returns Score as percentage (0-100) or null if no checks completed
 */
export const calculateInspectionScoreFromResults = (
  results: ComplianceAnswerOption[]
): number | null => {
  const passedCount = results.filter(
    (r) => r === ComplianceAnswerOption.passed
  ).length;

  const notPassedCount = results.filter(
    (r) => r === ComplianceAnswerOption.notPassed
  ).length;

  const totalChecked = passedCount + notPassedCount;

  if (totalChecked === 0) {
    return null;
  }

  return roundInspectionScorePercent((passedCount / totalChecked) * 100);
};

/**
 * Extract compliance check results from a form values object.
 */
export const extractComplianceResultsFromForm = (
  formValues: HashMap<unknown>
): ComplianceAnswerOption[] => {
  return Object.keys(formValues)
    .filter((key) => key.startsWith(`${COMPLIANCE_ANSWER_OPTION_PREFIX}-`))
    .map((key) => formValues[key])
    .filter(isScoredComplianceResult);
};

export function extractScoredComplianceResultsFromProtocol(
  protocol: InspectionScoreProtocolItem[],
  formValues?: HashMap<unknown>
): ComplianceAnswerOption[] {
  const results: ComplianceAnswerOption[] = [];

  protocol.forEach((protocolItem) => {
    const checklistItemId = checklistItemIdFromProtocolItem(protocolItem);
    const complianceAnswers = (protocolItem.answers ?? []).filter(
      isComplianceProtocolAnswer
    );

    if (complianceAnswers.length > 0) {
      complianceAnswers.forEach((answer) => {
        if (answer.isApplicable === false) {
          return;
        }

        const formResult = resolveFormComplianceResult(
          formValues,
          checklistItemId,
          answer.checklistAnswerId
        );
        const result = isScoredComplianceResult(formResult)
          ? formResult
          : answer.selectedOption;

        if (isScoredComplianceResult(result)) {
          results.push(result);
        }
      });
      return;
    }

    if (isScoredComplianceResult(protocolItem.complianceCheck?.result)) {
      results.push(protocolItem.complianceCheck.result);
    }
  });

  return results;
}

export function calculateInspectionScoreFromProtocol(
  protocol: InspectionScoreProtocolItem[],
  formValues?: HashMap<unknown>
): number | null {
  return calculateInspectionScoreFromResults(
    extractScoredComplianceResultsFromProtocol(protocol, formValues)
  );
}

/**
 * Calculate inspection score from form values containing compliance check results.
 * When protocol answers are present, `isApplicable=false` stays outside the denominator.
 */
export const calculateInspectionScoreFromForm = (
  formValues: HashMap<unknown>,
  protocol: InspectionScoreProtocolItem[] = []
): number | null => {
  if (protocol.length > 0) {
    return calculateInspectionScoreFromProtocol(protocol, formValues);
  }

  return calculateInspectionScoreFromResults(
    extractComplianceResultsFromForm(formValues)
  );
};
